import React, {useState} from "react";
import {useAuth} from "../../../providers/AuthProvider";
import {useMutation} from "@tanstack/react-query";
import Team from "../../../models/Team";
import {useParams, useSearchParams} from "react-router-dom";
import {Modal} from "antd";
import TeamDisciplineAssignmentForm from "./TeamDisciplineAssignmentForm";
import TeamDeliverableForm from "./TeamDeliverableForm";
import AgencyOpportunity from "../../../models/AgencyOpportunity";
import Opportunity from "../../../models/Opportunity";
import {useAPI} from "../../../config/API";


let TeamCandidateScopeContext = React.createContext(null);

const TeamCandidateScopeProvider = ({opportunityId, forAssigned, team, isDisabled, project, children, ...props}) => {

    const {auth} = useAuth();
    const {candidateId: paramId} = useParams();

    const candidateId = team ? team.job_candidate?.id : (forAssigned ? project.assigned_team_candidate?.job_candidate?.id : paramId);
    const [teamScopeModel, setTeamScopeModel] = useState( team ? Team.normalizeScope(Team.make({...team}), project) : null);
    const [searchParams, setSearchParams] = useSearchParams();

    const [editingTeamAssignment, setEditingTeamAssignment] = useState();
    const [editingTeamDeliverable, setEditingTeamDeliverable] = useState();
    const [showEditForm, setShowEditForm] = useState(false);
    const {
        data,
        isLoading: isLoadingCandidate,
        error,
        refetch
    } = useAPI(['fetchTeamCandidateScope', parseInt(candidateId)],  {
        enabled: !team,
        onSuccess: async (data) => {
            let scopedTeam = Team.normalizeScope(data, project)
            console.log('onSuccess Team Scope', scopedTeam);
            await setTeamScopeModel(scopedTeam);
        },
    })

    const updateQueryCallback = async (updatedTeam) => {
        console.log('TeamCandidateScopeProvider@updateCallback', updatedTeam?.job_candidate.id, candidateId)
        let scoped = Team.normalizeScope(Team.make({...updatedTeam}), project);

        // console.log('updateQueryCallback', scoped)
        // if (team) {

        // }
        await setTeamScopeModel(scoped);
        if (opportunityId) {
            let opp = await TB.queryClient.getQueryData(['fetchOpportunityForAgency', opportunityId])
            if (!opp) return;

            let updatedOpp = {...opp};
            updatedOpp.matching_talent = TB.map(updatedOpp.matching_talent, talent => {
                if (talent.job_candidate.id === parseInt(candidateId)) {
                    return {
                        ...talent,
                        job_candidate: {
                            ...talent.job_candidate,
                            ...scoped.job_candidate
                        }
                    };
                }
                return talent;
            })

            updatedOpp = AgencyOpportunity.make(updatedOpp);
            await Opportunity.refreshCacheFor(
                updatedOpp
            );
        }
        await TB.queryClient.setQueryData(['fetchTeamCandidateScope', parseInt(candidateId)], previousData => {
            if (!previousData) return undefined;

            return scoped
        })
        await TB.queryClient.invalidateQueries({
            predicate: ({queryKey}) => {
                return queryKey[0] === 'teamMatches'
            }
        })

    }
    const updateTeamScopeMutation = useMutation(
        ['updateTeamScope', candidateId],
        async (params) => {

            console.log('updateTeamScope', params);

            let request = TB.request(`/api/job-candidates/teams/${candidateId}/update-scope`, params).headers({
                'Content-Type': 'multipart/form-data'
            });

            let response = await request.post().catch(console.warn);

            return response
        }
    )

    const updateTeamTalentOppMutation = useMutation(
        ['updateTeamTalentOpp', candidateId],
        async (callback) => {

            return callback()
        },{
            onSuccess: async (updatedOpp) => {
                let oppIndex = TB.findIndex(teamScopeModel.job_candidate.talent_opps, ['id', updatedOpp.id])

                if (oppIndex < 0) {
                    throw new Error("Opp not found")
                }
                let updatedTeam = TB.set({...teamScopeModel}, `job_candidate.talent_opps[${oppIndex}]`, updatedOpp);

                updatedTeam = Team.normalizeScope(Team.make({...updatedTeam}),project);
                await updateQueryCallback(updatedTeam)
                return TB.toast("Sent reminder to Talent!")
            },
            onError: (error) => {
                console.warn('sendRemindToAccept', error);
                TB.showServerErrorToast()
            },
        }
    )

    const closeEditing = async () => {
        await setShowEditForm(false)
        await TB.wait(500)
        setEditingTeamDeliverable(null)
        setEditingTeamAssignment(null)
    }

    const cancelEdit = async () => {
        await closeEditing()
        let reset = Team.make(team ? {...team} : {...data});
        let resetTeamScope = Team.normalizeScope(reset, project);
        setTeamScopeModel(resetTeamScope)
    }

    const getScopedTalentUsers = () => {
        let validOpps = TB.filter([...teamScopeModel.job_candidate.talent_opps], opp => opp.status !== Opportunity.Statuses.Lost)
        return TB.map(validOpps, opp => {
            return {
                id: opp.user.id,
                disciplines: TB.map(opp.disciplines || [], dis => {
                    return {
                        id: dis.id,
                        hourly_rate: dis.hourly_rate,
                        deliverable_activities: TB.map(dis.deliverable_activities, activity => {
                            return {
                                id: activity.id,
                                total_hours: activity.total_hours
                            }
                        })
                    }
                })
            }
        })
    }

    const removeDisciplineAssignmentForTeamMember = async (disciplineId) => {
        let match = TB.find(teamScopeModel.disciplines, ['id', parseInt(disciplineId)]);
        if (!match) return;
        let confirm = await TB.alert('warning', 'Remove Team Member Assignment?', 'Are you sure you want to remove this team member from their assignment?', [
            {title: 'Yes, Continue'},
            {title: 'Cancel'}
        ])
        if (confirm) {
            let talent_users = TB.map(getScopedTalentUsers(), talent_user => {
                let disciplines = TB.filter([...(talent_user.disciplines || [])], dis => dis.id !== parseInt(disciplineId));

                return {
                    id: talent_user.id,
                    disciplines
                }
            })
            talent_users = TB.filter(talent_users, user => user.disciplines?.length > 0);

            return updateTeamScope({talent_users})

        }
    }

    const updateDisciplineAssignmentForTeamMember = async (disciplineId, params) => {

        let {
            message_to_talent,
            talent_user,
            hourly_rate,
            deliverable_activities
        } = params;

        deliverable_activities = TB.filter(deliverable_activities, act => !!act.total_hours && parseInt(act.total_hours) > 0)

        let assignment = {
            id: talent_user.id,
            disciplines: [{
                id: parseInt(disciplineId),
                hourly_rate,
                deliverable_activities: TB.map(deliverable_activities, act => ({id: act.id, total_hours: parseInt(act.total_hours)}))
            }]
        }

        let talent_users = TB.map(getScopedTalentUsers(), talent_user => {
            let disciplines = [...(talent_user.disciplines || [])]
            let matchedDisciplineIndex = TB.findIndex(disciplines, ['id', parseInt(disciplineId)]);
            if (matchedDisciplineIndex > -1) {
                if (assignment.id === talent_user.id) {
                    console.log('matchedDisciplineIndex', assignment)
                    disciplines[matchedDisciplineIndex] = assignment.disciplines[0]
                } else {
                    disciplines.splice(matchedDisciplineIndex, 1);
                }
            } else if (assignment.id === talent_user.id && disciplines.length && assignment.disciplines?.length) {
                disciplines = [...disciplines, ...assignment.disciplines]
            }

            return {
                id: talent_user.id,
                disciplines
            }
        })

        let existingUserIndex = TB.findIndex(talent_users, user => TB.find(user.disciplines, ['id', parseInt(disciplineId)]));

        if (existingUserIndex === -1) {
            talent_users.push(assignment);
        }
        talent_users = TB.filter(talent_users, user => user.disciplines?.length > 0);

        return talent_users
    }

    const updateDeliverableActivitiesForTeam = async (deliverable) => {

        let activitiesById = TB.keyBy(deliverable.activities, 'id');

        return TB.map(getScopedTalentUsers(), talent_user => {
            let disciplines = TB.map([...(talent_user.disciplines || [])], discipline => {

                let deliverable_activities = TB.map(discipline.deliverable_activities, activity => {
                    let total_hours = parseInt(activity.total_hours);
                    if (activitiesById[activity.id]) {
                        let matchedDiscipline = TB.find(activitiesById[activity.id].disciplines, ['id', discipline.id]);
                        if (matchedDiscipline) {
                            total_hours = matchedDiscipline?.total_team_hours
                        }
                    }
                    return {
                        ...activity,
                        total_hours
                    }
                })
                deliverable_activities = TB.filter(deliverable_activities, act => !!act.total_hours && parseInt(act.total_hours) > 0)
                return {
                    ...discipline,
                    deliverable_activities
                }
            })

            return {
                id: talent_user.id,
                disciplines
            }
        })
    }

    const onEditTeamAssignment = async (discipline) => {
        await setEditingTeamDeliverable(null);
        await setEditingTeamAssignment(discipline);
        // await TB.wait(500)
        await setShowEditForm(true)
        //
        // console.log('TeamMemberOppStatusTable',discipline, editingTeamAssignment)
        // let params = new URLSearchParams(searchParams);
        // params.set('manage-assignment', discipline.id);
        // return setSearchParams(params)
    }

    const onEditTeamDeliverable = async (deliverable) => {
        await setEditingTeamAssignment(null);
        await setEditingTeamDeliverable(deliverable);
        // await TB.wait(500)
        await setShowEditForm(true)
        //
        // console.log('TeamMemberOppStatusTable',discipline, editingTeamAssignment)
        // let params = new URLSearchParams(searchParams);
        // params.set('manage-assignment', discipline.id);
        // return setSearchParams(params)
    }
    // console.log('TeamCandidateScopeProvider', teamScopeModel)

    const confirmTeamScope = async() => {
        if (teamScopeModel.hasConfirmedScope()) {
            return TB.toast("Team Scope already confirmed.", {theme: 'danger'})
        }
        let missingDisciplines = TB.filter(teamScopeModel.disciplines, ['talent_opp', undefined])
        if (missingDisciplines?.length > 0) {

            await TB.alert('error', 'Missing Project Roles', 'The team scope must have a team member assigned to each role from the original project scope.', [
                {title: 'Ok'},
            ]);

            return;
        }
        let missingDeliverableHours = TB.find(TB.flatMap(teamScopeModel?.phases, 'deliverables'), del => del?.total_team_hours <= 0);
        if (missingDeliverableHours?.length > 0) {

            await TB.alert('error', 'Missing Deliverables', 'The team scope must have assigned hours for each deliverable from the original project scope.', [
                {title: 'Ok'}
            ]);

            return;
        }

        let updates = {talent_users: getScopedTalentUsers()};
        updates.scope_confirmed_at = new Date();

        let response = await updateTeamScope(updates, false);

        if (response && response.job_candidate?.has_confirmed_scope) {
            return TB.alert('success', 'Team Scope Confirmed', 'The team members have been invited to review and accept their team member brief.', [
                {title: 'Ok'},
            ]);
        }
    }

    const updateTeamScope = async (params, showSuccess = true) => {
        let response = await updateTeamScopeMutation.mutateAsync(params);
        if (TB.has(response, 'success.data.team')) {

            let updatedTeam = Team.make({...response.success.data.team});

            await updateQueryCallback(updatedTeam);
            if (showSuccess) {
                TB.toast("Team Scope Updated");
            }
            return updatedTeam;
        }
        TB.showServerErrorToast();
        return false;
    }

    const onContactTeamMember = (params) => {
        let talent_users = getScopedTalentUsers();
        TB.map(talent_users, user => {
            if (user.id === params.talent_user_id) {
                user.message_to_talent = params.message_to_talent;
            }
            return user
        })
        return updateTeamScope({talent_users})
    }

    const onUpdateProposal = async(params = {}) => {
        let updatedTeam = await updateTeamScope(params, false);

        if (updatedTeam) {
            TB.toast("Team Proposal Updated");
            return updatedTeam;
        }
        TB.showServerErrorToast();
    }
    const sendRemindToAccept = async(opp) => {

        if (opp.reminded_to_accept_at) {
            return TB.toast("Reminder Already Sent", {type:'info'})
        }
        return updateTeamTalentOppMutation.mutate(() => opp.sendRemindToAccept());
    }

    const uploadTalentContract = async(oppId, params = {}) => {
        if (!auth.canManageClients()) {
            return TB.showPermissionError();
        }

        return updateTeamTalentOppMutation.mutate(async() => {
            const uploadRequest = TB.request(
                TB.api_endpoint(
                    `/api/talent-user-opportunities/${oppId}/update-contract`
                ), params
            );
            console.log('uploadtalentcontract', {params, oppId})

            uploadRequest.headers({
                'Content-Type': 'multipart/form-data'
            });

            let response = await uploadRequest.post().catch(e => {
                console.warn("UPDATE", e)
            });

            console.log('Success', {response})
            if (!TB.has(response, 'success.data.opportunity')) {
                throw new Error('Something went wrong');
            }
            return Opportunity.make(response.success.data.opportunity)
        })
    }

    const value = {
        team: teamScopeModel,
        isLoading: !team && isLoadingCandidate,
        error,
        isDisabled: isDisabled || updateTeamScopeMutation.isLoading || updateTeamTalentOppMutation.isLoading,
        project: project,
        onChange: (props) => {
            let updatedTeam = Team.normalizeScope(Team.make({...teamScopeModel, ...props}),project);
            return updateQueryCallback(updatedTeam)
        },
        cancelEdit,
        sendRemindToAccept,
        onEditTeamAssignment,
        onEditTeamDeliverable,
        updateTeamScope,
        onUpdateProposal,
        removeDisciplineAssignmentForTeamMember,
        updateDisciplineAssignmentForTeamMember,
        updateDeliverableActivitiesForTeam,
        uploadTalentContract,
        confirmTeamScope,
        onContactTeamMember
    }

    return (
        <TeamCandidateScopeContext.Provider value={value}>

            {children}

            <Modal
                footer={null}
                // okText={editingTeamAssignment?.talent_opp ? 'Update' : 'Save'}
                title={editingTeamDeliverable ? (
                    <p className="text-xl">
                        <span>Edit Deliverable:&nbsp;</span>
                        <b className="text-primary-100">{editingTeamAssignment?.name}</b>
                    </p>
                ) : (editingTeamAssignment ? (
                        <p className="text-xl">
                            <span>Team Member Role:&nbsp;</span>
                            <b className="text-primary-100">{editingTeamAssignment?.name}</b>
                        </p>
                    ) : null
                )}
                className="project-scope-modal"
                open={showEditForm}
                closeable={true}
                afterClose={closeEditing}
                maskClosable={false}
                style={{top: 20}}
                destroyOnClose={true}
                width={1280}
                // onOk={updateTeamScope}
                confirmLoading={isDisabled}
                onCancel={cancelEdit}
            >

                {editingTeamDeliverable && (
                    <TeamDeliverableForm deliverable={editingTeamDeliverable} onCancel={cancelEdit} onClose={closeEditing}/>
                )}
                {editingTeamAssignment && (
                    <TeamDisciplineAssignmentForm discipline={editingTeamAssignment} onCancel={cancelEdit} onClose={closeEditing}/>
                )}
            </Modal>
        </TeamCandidateScopeContext.Provider>
    );
}

const useTeamCandidateScope = () => {
    return React.useContext(TeamCandidateScopeContext);
}

export default TeamCandidateScopeProvider;

export {useTeamCandidateScope, TeamCandidateScopeContext}